import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ViewEntity } from '@view-model/domain/view/ViewEntity';
import type { ViewModelEntity } from '@view-model/domain/view-model';
import type { Workspace } from '@workspace/domain/workspace';
import { faCopy } from '@fortawesome/free-regular-svg-icons';
import { faLink, faPen, faCog } from '@fortawesome/free-solid-svg-icons';
import { SidebarMenuItem } from './SidebarMenuItem';
import { SidebarMenuButton } from './SidebarMenuButton';
import { useViewModelOperationLogSender } from '@model-framework/action-log';
import { useCopyViewUrl } from '@view-model/ui/components/View/ViewTitleMenu/useCopyViewUrl';
import { ViewCollection } from '@view-model/domain/view/ViewCollection';
import { ViewSetting, ViewName } from '@view-model/domain/view';
import { ViewsClipboardOperator } from '@view-model/application/clipboard';
import { PositionSet } from '@view-model/models/common/PositionSet';
import { RTDBPath } from 'balus-common';
import { toast } from 'react-hot-toast';
import { ViewSettingModal } from '@view-model/ui/components/View/ViewSettingModal';
import { ViewTitleEditModal } from '@view-model/ui/components/View/ViewTitleEditModal';
import { ObjectRepository, TextRepository } from '@framework/repository';
import { Point } from '@view-model/models/common/basic';
import { usePrevious } from '@view-model/models/common/hooks/usePrevious';
import { useSidebarItemMenuPermissions } from './useSidebarItemMenuPermissions';
import { ViewItemTitle } from './ViewItemTitle';
import { useSidebarViewModel } from '../SidebarTree/useSidebarViewModel';

type Modals = 'ViewTitleEdit' | 'ViewSetting' | 'ViewDelete';

const useHandleModals = () => {
  const [openModal, setOpenModal] = useState<Modals | null>(null);

  return {
    openModal,
    handleViewTitleEditModalOpen: useCallback(() => {
      setOpenModal('ViewTitleEdit');
    }, []),
    handleViewSettingModalOpen: useCallback(() => {
      setOpenModal('ViewSetting');
    }, []),
    handleViewDeleteModalOpen: useCallback(() => {
      setOpenModal('ViewDelete');
    }, []),
    handleModalClose: useCallback(() => {
      setOpenModal(null);
    }, []),
  };
};

type Props = {
  view: ViewEntity;
  isSelected: boolean;
  workspace: Workspace;
  viewModel: ViewModelEntity;
  searchKeyword?: string;
};

export const ViewItem: React.FC<Props> = ({ view, isSelected, workspace, viewModel, searchKeyword }: Props) => {
  const { t } = useTranslation();
  const logSender = useViewModelOperationLogSender();
  const { isMenuButtonShowable, isEditMenuButtonShowable } = useSidebarItemMenuPermissions(workspace);
  const { openModal, handleViewTitleEditModalOpen, handleViewSettingModalOpen, handleModalClose } = useHandleModals();
  const { sortedViews } = useSidebarViewModel(viewModel.id, viewModel.workspaceId);
  const isMeaningPanel = view.viewType === 'meaningPanel';

  const handleCopyViewUrl = useCopyViewUrl(view.id);

  const handleCopyView = useCallback(async () => {
    // ビューのコピーにはビューのポジションが必要なので、あらかじめ取得しておく
    const viewPosition = await new ObjectRepository(Point, RTDBPath.View.positionPath(viewModel.id, view.id)).get();

    if (!viewPosition) {
      toast.error(t('common.message.copyFailed'));
      return;
    }
    const targetViews = [view];
    const targetViewPositions = { [view.id]: viewPosition };

    // 意味の構造パネルがあれば一緒にコピーする
    let meaningPanelView = null;
    let meaningPanelViewPosition = null;
    if (view.meaningPanelViewId) {
      meaningPanelView = sortedViews.find((v) => v.id === view.meaningPanelViewId);
      meaningPanelViewPosition = await new ObjectRepository(
        Point,
        RTDBPath.View.positionPath(viewModel.id, view.meaningPanelViewId)
      ).get();
    }
    if (meaningPanelView && meaningPanelViewPosition) {
      targetViews.push(meaningPanelView);
      targetViewPositions[meaningPanelView.id] = meaningPanelViewPosition;
    }

    const success = await ViewsClipboardOperator.copy(
      viewModel.id,
      new ViewCollection(targetViews),
      new PositionSet(targetViewPositions),
      null,
      logSender
    );

    if (success) {
      toast.success(t('common.message.copySuccess'));
    } else {
      toast.error(t('common.message.copyFailed'));
    }
  }, [logSender, sortedViews, view, viewModel.id, t]);

  const previousViewName = usePrevious(view.name);

  // タイトル変更
  const handleTitleSubmit = useCallback(
    async (newTitle: string) => {
      const viewNameRepo = new TextRepository(RTDBPath.View.namePath(viewModel.id, view.id));
      const newName = new ViewName(newTitle).trim();

      if (previousViewName && newName.isEquals(previousViewName)) {
        handleModalClose();
        return;
      }

      await viewNameRepo.save(newName.value);
      handleModalClose();
    },
    [viewModel.id, view.id, handleModalClose, previousViewName]
  );

  // 設定変更
  const handleViewSettingChanged = useCallback(
    async (checked: boolean, isLocked: boolean, isPositionFixed: boolean) => {
      const settingRepo = new ObjectRepository(ViewSetting, RTDBPath.View.settingPath(viewModel.id, view.id));
      const setting = new ViewSetting(checked, isLocked, isPositionFixed);
      await settingRepo.save(setting);
    },
    [view.id, viewModel.id]
  );

  return (
    <div className="relative">
      <SidebarMenuItem
        isSelected={isSelected}
        isMenuButtonShowable={isMenuButtonShowable}
        popupMenu={
          <>
            <SidebarMenuButton handleClick={handleCopyViewUrl} icon={faLink} title={t('canvas.view.copyLink.label')} />
            {!isMeaningPanel && (
              <SidebarMenuButton handleClick={handleCopyView} icon={faCopy} title={t('canvas.view.copy.label')} />
            )}
            {isEditMenuButtonShowable && !isMeaningPanel && (
              <>
                <SidebarMenuButton
                  handleClick={handleViewTitleEditModalOpen}
                  icon={faPen}
                  title={t('sidebar.viewItem.menu.editTitle')}
                  disabled={view.setting.isLocked || viewModel.isLocked}
                />
                <SidebarMenuButton
                  handleClick={handleViewSettingModalOpen}
                  icon={faCog}
                  title={t('canvas.viewSetting.label')}
                />
              </>
            )}
          </>
        }
      >
        <ViewItemTitle
          viewId={view.id}
          viewIsLocked={view.setting.isLocked}
          viewName={view.name.value}
          viewModelId={viewModel.id}
          searchKeyword={searchKeyword}
        />
      </SidebarMenuItem>

      {openModal === 'ViewTitleEdit' && (
        <ViewTitleEditModal
          isOpen={openModal === 'ViewTitleEdit'}
          currentName={view.name.value}
          onClose={handleModalClose}
          onSubmit={handleTitleSubmit}
        />
      )}

      {openModal === 'ViewSetting' && (
        <ViewSettingModal
          isOpen={openModal === 'ViewSetting'}
          viewSetting={view.setting}
          onClose={handleModalClose}
          onShowNodeCreatedUserChanged={handleViewSettingChanged}
        />
      )}
    </div>
  );
};
